import { Upload } from "@carbon/icons-react";
import { Button, ComboButton, MenuItem, TableToolbar, TableToolbarContent } from "@carbon/react";
import { NullableTableColumns, useModal } from "@flaminem/ui-components";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";

import { AuthorizedItem } from "src/authentication/AuthorizedMenu.tsx";
import { Scope, ScopeResource } from "src/authentication/PermissionsProvider.tsx";
import { usePermissions } from "src/authentication/usePermissions";
import { DimensionedLayout } from "src/components/DimensionedLayout/DimensionedLayout.tsx";
import { useDimensions } from "src/components/DimensionedLayout/useDimensions.ts";
import { Highlight } from "src/components/Highlight/Highlight.tsx";
import { ModalTransition } from "src/components/Modal";
import { FixedTable } from "src/components/Tables/FixedTable";
import { TableToolbarSearch } from "src/components/Tables/TableToolbarSearch";
import { useGetShortScenariosQuery } from "src/graphql/anti-fraud/queries/scenarios/GetShortScenariosQuery.ts";
import { useLocalSearchByFields } from "src/hooks/useLocalSearch.ts";
import { useFormatDate, useT } from "src/internationalization";
import { CreateScenarioModal } from "src/pages/Scenarios/CreateScenario/CreateScenarioModal";
import { ShortScenario } from "src/types/scenario.ts";

import { ImportVersionModal } from "./ImportVersionModal.tsx";
import { ScenarioActionsMenu } from "./ScenarioActionsMenu";

const ScenarioTab: React.FC = () => {
  const navigate = useNavigate();
  const { pageSize, dimensions } = useDimensions();

  const { hasPermission } = usePermissions();
  const params = useParams<{ code: string }>();
  const { data, loading, error, refetch } = useGetShortScenariosQuery({
    variables: {
      caseTypeCodes: params.code ? [params.code] : [],
    },
  });
  const t = useT();
  const formatDate = useFormatDate();

  const { filter, handleSearch, searchTerm } = useLocalSearchByFields([
    "name",
    "code",
    "threshold",
  ]);

  const {
    isModalOpen: isCreateModalOpen,
    openModal: openCreateModal,
    closeModal: closeCreateModal,
  } = useModal();

  const createScenarioVersionModal = useModal();

  const onRowClick = (id: string, caseTypecode: string) => {
    navigate(`/scenario/${caseTypecode}/${id}/active`);
  };

  const handleOnOpenImportVersionModal = () => {
    createScenarioVersionModal.openModal();
  };

  const handleOnCloseImportVersionModal = () => {
    refetch();
    createScenarioVersionModal.closeModal();
  };

  const hasPermissionToImport = hasPermission(ScopeResource.SCENARIO, Scope.IMPORT);

  const columns: NullableTableColumns<ShortScenario> = {
    name: {
      label: t("general.table.name.column.title"),
      render: (scenario) => (
        <Highlight text={scenario.name} term={searchTerm} className="truncate text-sm" />
      ),
      width: 100,
      flexBasis: 100,
      flexGrow: 2,
      flexShrink: 1,
    },
    code: {
      label: t("general.table.code.column.title"),
      render: (scenario) => (
        <Highlight text={scenario.code} term={searchTerm} className="truncate text-sm" />
      ),
      width: 100,
      flexBasis: 100,
      flexGrow: 2,
      flexShrink: 1,
    },
    threshold: {
      label: t("scenario.table.header.threshold"),
      render: (scenario) => (
        <Highlight
          text={scenario.threshold.toString()}
          term={searchTerm}
          className="truncate text-sm"
        />
      ),
      width: 100,
      flexBasis: 100,
      flexGrow: 2,
      flexShrink: 1,
    },
    rules: {
      label: t("scenario.table.header.rules"),
      render: (scenario) => scenario.rules.length,
      width: 100,
      flexBasis: 100,
      flexGrow: 2,
      flexShrink: 1,
    },
    created: {
      label: t("scenario.table.header.created"),
      render: (scenario) => formatDate(scenario.adminStatus.creation, { dateStyle: "medium" }),
      width: 100,
      flexBasis: 100,
      flexGrow: 2,
      flexShrink: 1,
    },
    actions: {
      width: 100,
      label: "",
      render: (scenario) => (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div onClick={(e) => e.stopPropagation()}>
          <ScenarioActionsMenu scenario={scenario} />
        </div>
      ),
    },
  };

  return (
    <>
      <DimensionedLayout>
        {dimensions?.width && (
          <FixedTable
            id="PagesNewScenario"
            data={data}
            columns={columns}
            filter={filter}
            isLoading={loading}
            pageSize={pageSize}
            isError={!!error}
            title={t("scenarios.title")}
            description={t("page.scenarios.tab.requests.table.description")}
            onRowClick={(row) => onRowClick(row.id, row.caseType.code)}
            toolbar={() => (
              <TableToolbar aria-label="data table toolbar">
                <TableToolbarContent>
                  <TableToolbarSearch search={searchTerm} onSearchChange={handleSearch} />
                  <div className="flex gap-2">
                    {/* don't display the combo button if the user has only the permission to create and edit */}
                    {!hasPermissionToImport && (
                      <AuthorizedItem
                        requiredPermissions={[
                          { resource: ScopeResource.SCENARIO, scopes: [Scope.CREATE, Scope.EDIT] },
                        ]}
                      >
                        <Button onClick={() => openCreateModal()}>
                          {t("scenario.table.button.create")}
                        </Button>
                      </AuthorizedItem>
                    )}

                    {hasPermissionToImport && (
                      <AuthorizedItem
                        requiredPermissions={[
                          {
                            resource: ScopeResource.SCENARIO,
                            scopes: [Scope.CREATE, Scope.EDIT, Scope.IMPORT],
                          },
                        ]}
                      >
                        <ComboButton
                          label={t("scenario.table.button.create")}
                          onClick={openCreateModal}
                          tooltipAlignment="top-right"
                        >
                          <MenuItem
                            renderIcon={Upload}
                            label={t("page.scenarios.tab.requests.table.button.import")}
                            onClick={handleOnOpenImportVersionModal}
                          />
                        </ComboButton>
                      </AuthorizedItem>
                    )}
                  </div>
                </TableToolbarContent>
              </TableToolbar>
            )}
          />
        )}
      </DimensionedLayout>

      <ModalTransition>
        {createScenarioVersionModal.isModalOpen && (
          <ImportVersionModal onClose={handleOnCloseImportVersionModal} />
        )}
      </ModalTransition>

      <ModalTransition>
        {isCreateModalOpen && (
          <CreateScenarioModal
            caseTypeCode={params.code ? params.code : ""}
            onClose={closeCreateModal}
            scenarios={data}
          />
        )}
      </ModalTransition>
    </>
  );
};

export default ScenarioTab;
